import service from "../../api/FetchInterceptor";
import {useEffect, useState} from "react";
import {Button, Checkbox, DatePicker, Drawer, Form, Input, message, Select, Upload} from "antd";
import {UploadOutlined} from "@ant-design/icons";

const formDataHeaders = {
    ...service.defaults.headers,
    "Content-Type": "multipart/form-data",
};

const AddDossier = ({open, setOpen, onSuccess}) => {
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();
    const [create, setCreate] = useState({
        typeDossier: [],
        typeMarche: [],
        natureMarche: [],
        typeFinancement: [],
        typeTransmission: [],
        autoriteContractante: null,
        codeFichier: null,
    });
    const [fileList, setFileList] = useState([]);

    const loadCreate = () => {
        service({
            method: "GET",
            url: "dossiers/create",
        }).then((response) => {
            setCreate(response);
        });
    };

    useEffect(() => {
        if (open) {
            loadCreate();
        } else {
            form.resetFields();
            setFileList([]);
        }
    }, [open]);

    // Chaque fichier est envoyé séparément (un fichier par requête, 200 Mo max)
    // dès qu'il est sélectionné, plutôt que d'être bufferisé puis envoyé avec le
    // reste du formulaire : ça évite de dépasser la limite d'upload de la
    // plateforme lorsque plusieurs pièces jointes sont ajoutées.
    const uploadFile = ({file, onSuccess, onError, onProgress}) => {
        service({
            method: "POST",
            url: "fichiers/saveFichier",
            data: {
                codeFichier: create.codeFichier,
                fichiers: [file],
            },
            headers: formDataHeaders,
            onUploadProgress: (event) => {
                if (event.total) {
                    onProgress({percent: Math.round((event.loaded / event.total) * 100)});
                }
            },
        })
            .then((response) => {
                onSuccess(response[0]);
            })
            .catch((error) => {
                onError(error);
                message.error(
                    error.response?.data?.message || `Erreur lors de l'envoi du fichier ${file.name}`
                );
            });
    };

    const uploadingCount = fileList.filter((f) => f.status === "uploading").length;

    const onRemoveFile = (file) => {
        const code = file.response?.code;

        if (!code) {
            return true;
        }

        return service({
            method: "DELETE",
            url: `fichiers/${code}`,
        })
            .then(() => true)
            .catch(() => {
                message.error("Erreur lors de la suppression de la pièce jointe");
                return false;
            });
    };

    const onSubmit = (values) => {
        if (uploadingCount > 0) {
            message.warning("Veuillez attendre la fin de l'envoi des fichiers");
            return;
        }

        setLoading(true);

        const payload = {
            ...values,
            date: values.date ? values.date.format("YYYY-MM-DD") : undefined,
            date_reception: values.date_reception
                ? values.date_reception.format("YYYY-MM-DD")
                : undefined,
            confidentiel: values.confidentiel ? 1 : 0,
            code_fichier: create.codeFichier,
        };

        service({
            method: "POST",
            url: "dossiers",
            data: payload,
        })
            .then((response) => {
                message.success({content: response.message, duration: 2});

                form.resetFields();
                setFileList([]);
                setOpen(false);
                onSuccess();
            })
            .catch((error) => {
                message.error(
                    error.response?.data?.message || "Erreur lors de l'enregistrement du dossier"
                );
            })
            .finally(() => {
                setLoading(false);
            });
    };

    return (
        <Drawer
            title="Enregistrer un dossier"
            open={open}
            onClose={() => setOpen(false)}
            width={700}
        >
            <Form form={form} onFinish={onSubmit} layout="vertical">
                <Form.Item label="Autorité contractante">
                    <Input value={create.autoriteContractante} disabled />
                </Form.Item>

                <Form.Item
                    name="reference"
                    label="Référence du BE (Bordereau d'envoie)"
                    rules={[{required: true}]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="objet"
                    label="Objet"
                    rules={[{required: true}]}
                >
                    <Input.TextArea rows={2} />
                </Form.Item>

                <Form.Item
                    name="type_dossier_code_reference"
                    label="Type de dossier"
                    rules={[{required: true}]}
                >
                    <Select
                        placeholder="Sélectionner le type de dossier"
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                            option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                    >
                        {create.typeDossier.map((t) => (
                            <Select.Option key={t.codereference} value={t.codereference}>
                                {t.libelle}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item name="type_marche_code_reference" label="Type de marché" rules={[{required: true}]}>
                    <Select placeholder="Sélectionner le type de marché" allowClear showSearch optionFilterProp="children">
                        {create.typeMarche.map((t) => (
                            <Select.Option key={t.codereference} value={t.codereference}>
                                {t.libelle}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item name="nature_marche_code_reference" label="Nature de dossier">
                    <Select placeholder="Sélectionner la nature de dossier" allowClear showSearch optionFilterProp="children">
                        {create.natureMarche.map((t) => (
                            <Select.Option key={t.codereference} value={t.codereference}>
                                {t.libelle}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item name="type_financement_code_reference" label="Type de financement" rules={[{required: true}]}>
                    <Select placeholder="Sélectionner le type de financement" allowClear showSearch optionFilterProp="children">
                        {create.typeFinancement.map((t) => (
                            <Select.Option key={t.codereference} value={t.codereference}>
                                {t.libelle}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>

                {/* <Form.Item name="type_transmission_code_reference" label="Mode de transmission">
                    <Select placeholder="Sélectionner le mode de transmission" allowClear showSearch optionFilterProp="children">
                        {create.typeTransmission.map((t) => (
                            <Select.Option key={t.codereference} value={t.codereference}>
                                {t.libelle}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item> */}

                <Form.Item name="numero_sigmap" label="Numéro SIGMAP" rules={[{required: true}]}>
                    <Input />
                </Form.Item>

                <Form.Item
                    name="date"
                    label="Date d'emission du BE (Bordereau d'envoie)"
                    rules={[{required: true}]}
                >
                    <DatePicker style={{width: "100%"}} format="DD/MM/YYYY" />
                </Form.Item>

                {/* <Form.Item name="date_reception" label="Date de réception">
                    <DatePicker style={{width: "100%"}} format="DD/MM/YYYY" />
                </Form.Item> */}

                {/* <Form.Item name="confidentiel" valuePropName="checked">
                    <Checkbox>Dossier confidentiel</Checkbox>
                </Form.Item>

                <Form.Item name="contenu" label="Contenu / observations">
                    <Input.TextArea rows={3} />
                </Form.Item> */}

                <Form.Item
                    label="Pièces jointes (Pdf, Word, Excel, Image)"
                    extra="Taille maximale de 200 Mo par fichier"
                >
                    <Upload
                        multiple
                        fileList={fileList}
                        onChange={({fileList: newFileList}) => setFileList(newFileList)}
                        customRequest={uploadFile}
                        onRemove={onRemoveFile}
                        disabled={!create.codeFichier}
                    >
                        <Button icon={<UploadOutlined />} disabled={!create.codeFichier}>
                            Sélectionnez les fichiers
                        </Button>
                    </Upload>
                    {uploadingCount > 0 && (
                        <div style={{marginTop: 8, color: "#1677ff"}}>
                            Envoi de {uploadingCount} fichier{uploadingCount > 1 ? "s" : ""} en
                            cours, veuillez patienter avant d'enregistrer...
                        </div>
                    )}
                </Form.Item>

                <Button
                    loading={loading}
                    disabled={uploadingCount > 0}
                    type="primary"
                    htmlType="submit"
                >
                    Enregistrer
                </Button>
            </Form>
        </Drawer>
    );
};

export default AddDossier;
